import { useCallback, useEffect, useState } from "react";
import { toast, type FileObjectType } from "@jsq/ds/core";
import {
  useFinpubGeneratePresignedUrlsMutation,
  usePreviewDataPackageImportLazyQuery,
} from "~/graphql/supergraphHooks";
import { uploadFilesToS3 } from "~/utils/s3UploadClient";
import {
  getAcceptedUploadFile,
  getRejectedUploadFiles,
  type RejectedUploadFile,
} from "./uploadFiles";
import type { TypeHierarchy } from "./DataPackageSlideout/HierarchyTreeGrid";
import type { RowError } from "./ImportErrorsBanner";

export const INVALID_HIERARCHY_TEMPLATE_ERROR =
  "Use a hierarchy template to ensure correct formatting.";

export const buildInvalidHierarchyTemplateError = (serverMessage?: string | null) => {
  const trimmed = serverMessage?.trim();
  return trimmed
    ? `${trimmed}. ${INVALID_HIERARCHY_TEMPLATE_ERROR}`
    : INVALID_HIERARCHY_TEMPLATE_ERROR;
};

export const useBeforeUnloadPrompt = (enabled: boolean) => {
  useEffect(() => {
    if (!enabled) return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [enabled]);
};

export type HierarchyUpload = {
  uploadedFile: FileObjectType | null;
  serverRejectedFiles: RejectedUploadFile[] | undefined;
  s3Key: string | null;
  previewData: TypeHierarchy | null;
  rowErrors: RowError[];
  nodeCount: number;
  preSignedErrorReportUrl: string | null;
  isUploading: boolean;
  handleFileSelected: (files: FileObjectType[]) => Promise<void>;
  reset: () => void;
};

export const useHierarchyUpload = (arenaId?: number | null): HierarchyUpload => {
  const [uploadedFile, setUploadedFile] = useState<FileObjectType | null>(null);
  const [serverRejectedFiles, setServerRejectedFiles] = useState<RejectedUploadFile[]>();
  const [s3Key, setS3Key] = useState<string | null>(null);
  const [previewData, setPreviewData] = useState<TypeHierarchy | null>(null);
  const [rowErrors, setRowErrors] = useState<RowError[]>([]);
  const [nodeCount, setNodeCount] = useState(0);
  const [preSignedErrorReportUrl, setPreSignedErrorReportUrl] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const [finpubGeneratePresignedUrls] = useFinpubGeneratePresignedUrlsMutation();
  const [fetchPreview] = usePreviewDataPackageImportLazyQuery();

  const clearPreview = useCallback(() => {
    setPreviewData(null);
    setRowErrors([]);
    setNodeCount(0);
    setPreSignedErrorReportUrl(null);
    setS3Key(null);
  }, []);

  const reset = useCallback(() => {
    setUploadedFile(null);
    setServerRejectedFiles(undefined);
    clearPreview();
  }, [clearPreview]);

  const handleFileSelected = useCallback(
    async (files: FileObjectType[]) => {
      const rejectedFiles = getRejectedUploadFiles(files);
      if (rejectedFiles) {
        setServerRejectedFiles(rejectedFiles);
        return;
      }

      const acceptedFile = getAcceptedUploadFile(files);
      if (!acceptedFile) {
        setUploadedFile(null);
        setServerRejectedFiles(undefined);
        clearPreview();
        return;
      }

      setUploadedFile(acceptedFile);
      setServerRejectedFiles(undefined);
      setIsUploading(true);
      clearPreview();

      try {
        const { file } = acceptedFile;
        const presignedResponse = await finpubGeneratePresignedUrls({
          variables: {
            inputs: [
              {
                fileName: file.name,
                contentType: file.type || "application/octet-stream",
              },
            ],
            arenaId: arenaId ?? undefined,
          },
        });

        const presignedObjs = presignedResponse.data?.finpubGeneratePresignedUrls;
        if (!presignedObjs || presignedObjs.length === 0) {
          throw new Error("Failed to generate upload URL");
        }

        const { objectKey, s3Url } = presignedObjs[0];
        const uploadResults = await uploadFilesToS3([
          { generatedUrlObj: { objectKey, s3Url }, file },
        ]);

        if (uploadResults.some((r) => r.response.status !== 200)) {
          throw new Error("Failed to upload file to S3");
        }

        setS3Key(objectKey);

        const { data } = await fetchPreview({
          variables: { s3Key: objectKey },
        });

        const preview = data?.finpubDataPackagePreviewImport;
        const typeHierarchy = preview?.typeHierarchy as TypeHierarchy | undefined;
        if (typeHierarchy?.tree_structure?.root) {
          setPreviewData(typeHierarchy);
        } else {
          toast.error(buildInvalidHierarchyTemplateError(preview?.message));
          setUploadedFile(null);
          setS3Key(null);
        }
        setRowErrors(preview?.rowErrors ?? []);
        setNodeCount(preview?.nodeCount ?? 0);
        setPreSignedErrorReportUrl(preview?.preSignedErrorReportUrl ?? null);
      } catch {
        toast.error("Failed to upload file. Please try again.");
        setUploadedFile(null);
        setS3Key(null);
      } finally {
        setIsUploading(false);
      }
    },
    [finpubGeneratePresignedUrls, fetchPreview, clearPreview, arenaId],
  );

  return {
    uploadedFile,
    serverRejectedFiles,
    s3Key,
    previewData,
    rowErrors,
    nodeCount,
    preSignedErrorReportUrl,
    isUploading,
    handleFileSelected,
    reset,
  };
};
